import { Link } from '@tanstack/react-router'
import { ReactNode } from 'react'

import NoSearchResultImage from '@/assets/no_search_result.svg'
import { ArrowForwardIcon, MapPinIcon } from '@/components/icon/generated'
import {
  Badge,
  Box,
  Center,
  Flex,
  For,
  HStack,
  LinkBox,
  LinkOverlay,
  SegmentedControlsContent,
  SegmentedControlsList,
  SegmentedControlsRoot,
  SegmentedControlsTrigger,
  Stack,
  Text,
} from '@/components/ui'
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPageText,
  PaginationPrevTrigger,
  PaginationRoot,
  PaginationRootProps,
} from '@/components/ui/pagination'
import { TRIAL_PROGRESS_LABELS } from '@/constants/trialDetailLabels'
import { useBreakpointValue } from '@/lib/chakra-ui'
import { FragmentType, getFragmentData, graphql } from '@/lib/gql'
import { formatDate, lastUpdatedLabel } from '@/utils/datetime'

const trialListItemFragment = graphql(`
  fragment TrialListItem on Trial {
    isNew
    lastPublishedDate
    trialNumber
    latestDetail {
      plainTrialName
      progress
      targetDiseaseName
      trialSite
    }
    latestCount {
      openCaseCount
      savedAt
    }
  }
`)
type TrialListItemProps = {
  trialListItem: FragmentType<typeof trialListItemFragment>
}

const TrialListItem = ({ trialListItem }: TrialListItemProps) => {
  const isDesktop = useBreakpointValue({ base: false, desktop: true })

  const listItemData = getFragmentData(trialListItemFragment, trialListItem)

  const {
    trialNumber,
    isNew,
    lastPublishedDate,
    latestDetail: { progress, plainTrialName, targetDiseaseName, trialSite },
    latestCount: { openCaseCount, savedAt },
  } = listItemData

  return (
    <LinkBox
      as={HStack}
      p={4}
      gap={4}
      justifyContent="space-between"
      borderBottom="1px solid"
      borderColor="gray.200"
      bgColor="transparent"
      bgPos="center"
      transition="background 0.8s"
      css={{
        '--hover-bg-color': '{colors.blue.500/4}',
        '&:hover': {
          background:
            'var(--hover-bg-color) radial-gradient(circle, var(--hover-bg-color) 0.1%, transparent 0.1%) center/150000%',
        },
        '&:active': {
          bgColor: 'blue.500/8',
          backgroundSize: '100%',
          transition: 'background 0s',
        },
      }}
    >
      <Stack flex={1} minW={0} gap={{ base: 2, desktop: 1 }}>
        <Stack
          direction={{ base: 'column', desktop: 'row' }}
          gap={{ base: 1, desktop: 4 }}
          gapY={{ desktop: 1 }}
          flexWrap={{ desktop: 'wrap' }}
        >
          <HStack gap={0}>
            <HStack gap={2}>
              <Text>試験ID</Text>
              <Text>:</Text>
              <Text as="span" fontWeight="semibold">
                <LinkOverlay asChild>
                  <Link to="/trials/$trialNumber" params={{ trialNumber }}>
                    {trialNumber}
                  </Link>
                </LinkOverlay>
              </Text>
            </HStack>
          </HStack>
          <HStack gap={{ base: 2, desktop: 4 }}>
            {(isNew || progress) && (
              <HStack gap="SS">
                {isNew && (
                  <Badge colorPalette="red" variant="outline">
                    新規
                  </Badge>
                )}
                {progress && (
                  <Badge colorPalette="gray" variant="outline">
                    {TRIAL_PROGRESS_LABELS[progress]}
                  </Badge>
                )}
              </HStack>
            )}
            {lastPublishedDate && (
              <HStack gap={1}>
                <Text fontSize="sm">最終公開日</Text>
                <Text fontSize="sm">:</Text>
                <Text fontSize="sm">{formatDate(lastPublishedDate)}</Text>
              </HStack>
            )}
          </HStack>
        </Stack>

        <Text
          fontWeight="semibold"
          whiteSpace="pre-wrap"
          overflow="hidden"
          display="-webkit-box"
          css={{
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: isDesktop ? 2 : 3,
          }}
        >
          {plainTrialName}
        </Text>
        <Text fontWeight="semibold" whiteSpace="pre-wrap">
          {targetDiseaseName}
        </Text>

        <Stack
          direction={{ base: 'column', desktop: 'row' }}
          gap={{ base: 0 }}
          justifyContent="space-between"
        >
          <HStack gap={1}>
            {trialSite && (
              <>
                <Box color="gray.500">
                  <MapPinIcon size="20px" />
                </Box>
                <Text>{trialSite}</Text>
              </>
            )}
          </HStack>

          {openCaseCount > 0 && savedAt && (
            <Stack
              direction={{ base: 'column', desktop: 'row' }}
              gap="SS"
              alignItems="baseline"
            >
              <HStack gap={2}>
                <Text fontSize="sm">募集症例数</Text>
                <Text fontSize={{ base: 'xl', desktop: '2xl' }}>
                  {openCaseCount}
                </Text>
                <Text fontSize="sm">例</Text>
              </HStack>
              <Text fontSize={{ base: 'xs', desktop: 'sm' }}>
                （最終更新{lastUpdatedLabel(savedAt)}）
              </Text>
            </Stack>
          )}
        </Stack>
      </Stack>

      <ArrowForwardIcon color="var(--chakra-colors-gray-500)" />
    </LinkBox>
  )
}

const NoSearchResult = () => {
  // GlobalHeader(64px) と Pagination(56px) ぶんの高さを引く
  return (
    <Center h="calc(100svh - 120px)">
      <Stack gap={2} alignItems="center">
        <NoSearchResultImage />
        <Text color="blue.400">一致する検索結果がありません</Text>
      </Stack>
    </Center>
  )
}

const trialListFragment = graphql(`
  fragment TrialList on TrialsPayload {
    trials {
      trialUid
      ...TrialListItem
    }
    totalCount
  }
`)

type TrialListProps = {
  trials: FragmentType<typeof trialListFragment>
  pageChangeHandler: PaginationRootProps['onPageChange']
}

export const TrialList = ({ trials, pageChangeHandler }: TrialListProps) => {
  const trialsData = getFragmentData(trialListFragment, trials)
  return (
    <Flex
      direction="column"
      h="full"
      bgColor="white"
      borderRight="1px solid"
      borderColor="border.gray"
    >
      <Stack
        direction={{ base: 'column', desktop: 'row' }}
        px="L"
        py={3}
        justifyContent="space-between"
        bgColor="white"
      >
        <Text fontSize="lg" fontWeight="semibold" color="gray.600">
          試験一覧
        </Text>
        <PaginationRoot
          count={trialsData.totalCount}
          pageSize={20}
          onPageChange={pageChangeHandler}
        >
          <HStack justifyContent="space-between">
            <PaginationPageText />
            <HStack>
              <PaginationPrevTrigger />
              <PaginationItems />
              <PaginationNextTrigger />
            </HStack>
          </HStack>
        </PaginationRoot>
      </Stack>

      <Box px="4" overflow="auto" flex="1">
        <For each={trialsData.trials} fallback={<NoSearchResult />}>
          {(trial, i) => (
            <TrialListItem
              key={trialsData.trials[i].trialUid} // trialUidを使っていないというwarningが出るため無理やりindexでアクセス
              trialListItem={trial}
            />
          )}
        </For>
      </Box>
    </Flex>
  )
}

type SPTrialListProps = TrialListProps & {
  inquiryArea: ReactNode
}

export const SPTrialList = ({
  trials,
  pageChangeHandler,
  inquiryArea,
}: SPTrialListProps) => {
  return (
    <SegmentedControlsRoot
      size="md"
      h="full"
      defaultValue="trialList"
      display="flex"
      flexDirection="column"
    >
      <SegmentedControlsList w="full" flexShrink={0} px={4} py={2}>
        <SegmentedControlsTrigger
          variant="soft"
          value="trialList"
          flex={1}
          justifyContent="center"
        >
          試験一覧
        </SegmentedControlsTrigger>
        <SegmentedControlsTrigger
          variant="soft"
          value="inquiry"
          flex={1}
          justifyContent="center"
        >
          すべての問い合わせ
        </SegmentedControlsTrigger>
      </SegmentedControlsList>

      <SegmentedControlsContent
        h="full"
        overflow="auto"
        flexGrow={1}
        py={0}
        value="trialList"
      >
        <TrialList trials={trials} pageChangeHandler={pageChangeHandler} />
      </SegmentedControlsContent>
      <SegmentedControlsContent
        h="full"
        overflow="auto"
        flexGrow={1}
        py={0}
        value="inquiry"
      >
        {inquiryArea}
      </SegmentedControlsContent>
    </SegmentedControlsRoot>
  )
}
